import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { timeAgo, isArchivedMission } from '../../lib/utils';
import { useMissions } from '../../hooks/useMissions';
import { useAuth } from '../../context/AuthContext';
import {
  Briefcase, Users, Clock, Target, Plus, Zap, CheckCircle2,
  ChevronRight, Calendar, UploadCloud, Loader2, UserPlus,
  TrendingUp, Building2, Info, TriangleAlert,
  Star, BarChart3,
} from 'lucide-react';
import { useOrganization } from '../../hooks/useOrganization';

interface FeedItem {
  id: string;
  iconEl: React.ReactNode;
  bgClass: string;
  text: React.ReactNode;
  time: number;
  path?: string;
  badge?: string;
  badgeClass?: string;
}

const Tooltip: React.FC<{ text: string; children: React.ReactNode }> = ({ text, children }) => (
  <div className="relative group/tip inline-flex">
    {children}
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 hidden group-hover/tip:block w-52 pointer-events-none">
      <div className="bg-slate-800 text-white text-[11px] rounded-lg px-3 py-2 shadow-xl leading-relaxed">
        {text}
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
      </div>
    </div>
  </div>
);

const HomeDashboardView: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { org } = useOrganization();
  const orgName = org?.name ?? 'Organisation';
  const { missions } = useMissions('org');
  const { missions: portfolioMissions, isLoading: isPortfolioLoading } = useMissions('portfolio', user?.id);

  const firstName = user?.user_metadata?.full_name?.split(' ')[0]
    || user?.user_metadata?.name?.split(' ')[0]
    || user?.email?.split('@')[0]
    || 'vous';

  const today = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const capitalizedDate = today.charAt(0).toUpperCase() + today.slice(1);

  const [feedTab, setFeedTab] = useState<'portfolio' | 'org'>('portfolio');
  const [indicateursTab, setIndicateursTab] = useState<'portfolio' | 'org'>('portfolio');

  // ── State ─────────────────────────────────────────────────────────────────
  const [matchingTasksCount, setMatchingTasksCount] = useState<number | null>(null);
  const [avgScore, setAvgScore] = useState<number | null>(null);
  const [bestScore, setBestScore] = useState<number | null>(null);
  const [portfolioAvgScore, setPortfolioAvgScore] = useState<number | null>(null);
  const [portfolioBestScore, setPortfolioBestScore] = useState<number | null>(null);
  const [portfolioMatchingTasksCount, setPortfolioMatchingTasksCount] = useState<number | null>(null);
  const [analyzedMissionIds, setAnalyzedMissionIds] = useState<Set<string>>(new Set());
  const [portfolioCandidateCount, setPortfolioCandidateCount] = useState<number | null>(null);
  const [portfolioCandidatesWithoutHighMatch, setPortfolioCandidatesWithoutHighMatch] = useState<number | null>(null);
  const [newPortfolioCandidates7d, setNewPortfolioCandidates7d] = useState<number | null>(null);
  const [orgCandidateCount, setOrgCandidateCount] = useState<number | null>(null);
  const [orgCandidatesWithoutHighMatch, setOrgCandidatesWithoutHighMatch] = useState<number | null>(null);

  // Feed state
  const [recentCandidates, setRecentCandidates] = useState<{ id: number; full_name: string | null; created_at: string }[]>([]);
  const [recentPortfolioCandidates, setRecentPortfolioCandidates] = useState<{ id: number; full_name: string | null; created_at: string }[]>([]);
  const [recentTasks, setRecentTasks] = useState<{ id: number; mission_id: number | null; created_at: string }[]>([]);

  // ── Org-level fetches ─────────────────────────────────────────────────────
  useEffect(() => {
    // Org candidates for org feed
    supabase
      .from('candidates')
      .select('id, full_name, created_at')
      .order('created_at', { ascending: false })
      .limit(5)
      .then(({ data }) => setRecentCandidates(data || []));

    // Matching tasks
    supabase
      .from('matching_tasks')
      .select('id, mission_id, created_at', { count: 'exact' })
      .order('created_at', { ascending: false })
      .then(({ data, count }) => {
        setMatchingTasksCount(count ?? 0);
        setRecentTasks((data || []).slice(0, 5));
        setAnalyzedMissionIds(new Set((data || []).map(t => String(t.mission_id))));
      });

    // Avg match score
    supabase
      .from('matches')
      .select('match_score')
      .order('created_at', { ascending: false })
      .limit(100)
      .then(({ data }) => {
        if (data && data.length > 0) {
          const avg = data.reduce((s: number, m: { match_score?: number }) => s + (m.match_score ?? 0), 0) / data.length;
          setAvgScore(Math.round(avg));
        } else setAvgScore(0);
      });

    // Best score
    supabase
      .from('matches')
      .select('match_score')
      .order('match_score', { ascending: false })
      .limit(1)
      .then(({ data }) => setBestScore(data?.[0]?.match_score ?? null));

    // Org total candidates count + candidates without high match
    supabase
      .from('candidates')
      .select('id', { count: 'exact' })
      .then(async ({ data, count }) => {
        setOrgCandidateCount(count ?? 0);
        if (!data || data.length === 0) { setOrgCandidatesWithoutHighMatch(0); return; }
        const allIds = data.map((c: { id: number }) => c.id);
        const { data: highMatchData } = await supabase
          .from('matches')
          .select('candidate_id')
          .in('candidate_id', allIds)
          .gte('match_score', 80);
        const highMatchIds = new Set((highMatchData || []).map((m: { candidate_id: number }) => m.candidate_id));
        setOrgCandidatesWithoutHighMatch(allIds.filter(id => !highMatchIds.has(id)).length);
      });
  }, []);

  // ── Portfolio-level fetches ───────────────────────────────────────────────
  useEffect(() => {
    if (!user?.id) return;

    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

    // Portfolio candidates — count + feed + 7d
    supabase
      .from('candidates')
      .select('id, full_name, created_at', { count: 'exact' })
      .eq('assigned_to', user.id)
      .order('created_at', { ascending: false })
      .then(async ({ data, count }) => {
        const all = data || [];
        setPortfolioCandidateCount(count ?? 0);
        setRecentPortfolioCandidates(all.slice(0, 5));
        setNewPortfolioCandidates7d(all.filter(c => c.created_at >= sevenDaysAgo).length);

        // Talents sans match > 80%
        if (all.length === 0) { setPortfolioCandidatesWithoutHighMatch(0); return; }
        const portfolioIds = all.map(c => c.id);
        const { data: highMatchData } = await supabase
          .from('matches')
          .select('candidate_id')
          .in('candidate_id', portfolioIds)
          .gte('match_score', 80);
        const highMatchIds = new Set((highMatchData || []).map(m => m.candidate_id));
        setPortfolioCandidatesWithoutHighMatch(portfolioIds.filter(id => !highMatchIds.has(id)).length);
      });
  }, [user?.id]);

  // ── Portfolio fetches (when portfolio missions loaded) ────────────────────
  useEffect(() => {
    if (portfolioMissions.length === 0) {
      setPortfolioAvgScore(0);
      setPortfolioBestScore(null);
      setPortfolioMatchingTasksCount(0);
      return;
    }
    const ids = portfolioMissions.map(m => Number(m.id));

    // Avg score portefeuille
    supabase
      .from('matches')
      .select('match_score')
      .in('mission_id', ids)
      .order('created_at', { ascending: false })
      .limit(100)
      .then(({ data }) => {
        if (data && data.length > 0) {
          const avg = data.reduce((s: number, m: { match_score?: number }) => s + (m.match_score ?? 0), 0) / data.length;
          setPortfolioAvgScore(Math.round(avg));
        } else setPortfolioAvgScore(0);
      });

    // Best score portefeuille
    supabase
      .from('matches')
      .select('match_score')
      .in('mission_id', ids)
      .order('match_score', { ascending: false })
      .limit(1)
      .then(({ data }) => setPortfolioBestScore(data?.[0]?.match_score ?? null));

    // Analyses lancées portefeuille
    supabase
      .from('matching_tasks')
      .select('id', { count: 'exact' })
      .in('mission_id', ids)
      .then(({ count }) => setPortfolioMatchingTasksCount(count ?? 0));
  }, [portfolioMissions]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const activePortfolioMissions = useMemo(
    () => portfolioMissions.filter(m => !isArchivedMission(m)),
    [portfolioMissions]
  );

  const portfolioMissionsWithoutAnalysis = useMemo(
    () => activePortfolioMissions.filter(m => !analyzedMissionIds.has(String(m.id))).length,
    [activePortfolioMissions, analyzedMissionIds]
  );

  const newPortfolioMissions7d = useMemo(() => {
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return portfolioMissions.filter(m => new Date(m.datePosted).getTime() >= sevenDaysAgo).length;
  }, [portfolioMissions]);

  const timeSavedMinutes = (matchingTasksCount ?? 0) * 20;
  const timeSavedDisplay = timeSavedMinutes === 0 ? '0min'
    : timeSavedMinutes < 60 ? `${timeSavedMinutes}min`
    : `${Math.floor(timeSavedMinutes / 60)}h${timeSavedMinutes % 60 > 0 ? String(timeSavedMinutes % 60).padStart(2, '0') : ''}`;

  const portfolioTimeSavedMinutes = (portfolioMatchingTasksCount ?? 0) * 20;
  const portfolioTimeSavedDisplay = portfolioTimeSavedMinutes === 0 ? '0min'
    : portfolioTimeSavedMinutes < 60 ? `${portfolioTimeSavedMinutes}min`
    : `${Math.floor(portfolioTimeSavedMinutes / 60)}h${portfolioTimeSavedMinutes % 60 > 0 ? String(portfolioTimeSavedMinutes % 60).padStart(2, '0') : ''}`;

  const activeMissions = useMemo(() => missions.filter(m => !isArchivedMission(m)), [missions]);
  const coverageRate = useMemo(() => {
    if (activeMissions.length === 0) return 0;
    const analyzed = activeMissions.filter(m => analyzedMissionIds.has(String(m.id))).length;
    return Math.round((analyzed / activeMissions.length) * 100);
  }, [activeMissions, analyzedMissionIds]);

  const portfolioCoverageRate = useMemo(() => {
    if (activePortfolioMissions.length === 0) return 0;
    const analyzed = activePortfolioMissions.filter(m => analyzedMissionIds.has(String(m.id))).length;
    return Math.round((analyzed / activePortfolioMissions.length) * 100);
  }, [activePortfolioMissions, analyzedMissionIds]);

  // ── Onboarding ────────────────────────────────────────────────────────────
  // Ces étapes décrivent l'avancement de l'ORGANISATION, pas celui de la
  // personne connectée : missions, candidats et analyses sont ceux de l'org.
  // Quelqu'un qui rejoint une équipe déjà lancée les trouve donc cochées sans
  // avoir rien fait — d'où le titre, qui dit de quoi on parle.
  const hasMissions = missions.length > 0;
  const hasCandidates = (portfolioCandidateCount ?? 0) > 0;
  const hasMatchingTasks = (matchingTasksCount ?? 0) > 0;
  const onboardingSteps = [
    { label: "Créer l'organisation", done: true, path: null },
    { label: 'Créer une première mission', done: hasMissions, path: '/missions' },
    { label: 'Importer votre premier candidat', done: hasCandidates, path: '/talents' },
    { label: 'Lancer votre première analyse IA', done: hasMatchingTasks, path: '/matching' },
  ];
  const completedCount = onboardingSteps.filter(s => s.done).length;
  const allDone = completedCount === onboardingSteps.length;
  const nextStep = onboardingSteps.find(s => !s.done);
  // Un bloc de démarrage n'a de sens que pendant le démarrage, et pour qui en a
  // la charge. Une fois l'organisation lancée, ou vue par quelqu'un qui vient de
  // la rejoindre, il n'affiche que des cases cochées par d'autres.
  const showOnboarding = !allDone && (org?.role === 'owner' || org?.role === 'admin');

  const getStatusBadge = (status?: string) => {
    if (!status) return 'bg-slate-100 text-slate-500';
    if (status === 'En cours') return 'bg-emerald-100 text-emerald-700';
    if (status === 'En attente') return 'bg-amber-100 text-amber-700';
    if (isArchivedMission({ status })) return 'bg-slate-100 text-slate-400';
    return 'bg-blue-100 text-blue-700';
  };

  // ── Feeds ─────────────────────────────────────────────────────────────────
  const portfolioMissionIds = useMemo(
    () => new Set(portfolioMissions.map(m => String(m.id))),
    [portfolioMissions]
  );

  const portfolioActivityFeed = useMemo<FeedItem[]>(() => {
    const items: FeedItem[] = [];
    for (const t of recentTasks.filter(t => portfolioMissionIds.has(String(t.mission_id)))) {
      const mission = portfolioMissions.find(m => m.id === String(t.mission_id));
      items.push({ id: `task-${t.id}`, iconEl: <Zap size={15} fill="currentColor" className="text-amber-500" />, bgClass: 'bg-amber-100', text: <>Analyse IA pour <span className="font-semibold text-indigo-600">{mission?.title ?? `Mission #${t.mission_id}`}</span></>, time: new Date(t.created_at).getTime(), path: `/matching/${t.id}` });
    }
    for (const c of recentPortfolioCandidates) {
      items.push({ id: `cand-${c.id}`, iconEl: <UserPlus size={15} className="text-indigo-500" />, bgClass: 'bg-indigo-100', text: <><span className="font-bold text-slate-900">{c.full_name}</span> ajouté à votre portefeuille</>, time: new Date(c.created_at).getTime(), path: `/talents/${c.id}` });
    }
    for (const m of portfolioMissions.slice(0, 5)) {
      items.push({ id: `mission-${m.id}`, iconEl: <Briefcase size={15} className="text-blue-500" />, bgClass: 'bg-blue-100', text: <>Mission <span className="font-semibold text-slate-900">{m.title}</span> — <span className="text-slate-500">{m.client}</span></>, time: new Date(m.datePosted).getTime(), path: `/missions/${m.id}`, badge: m.status || 'Nouveau', badgeClass: getStatusBadge(m.status) });
    }
    return items.sort((a, b) => b.time - a.time).slice(0, 8);
  }, [recentTasks, recentPortfolioCandidates, portfolioMissions, portfolioMissionIds]);

  const orgActivityFeed = useMemo<FeedItem[]>(() => {
    const items: FeedItem[] = [];
    for (const t of recentTasks) {
      const mission = missions.find(m => m.id === String(t.mission_id));
      items.push({ id: `task-${t.id}`, iconEl: <Zap size={15} fill="currentColor" className="text-amber-500" />, bgClass: 'bg-amber-100', text: <>Analyse IA pour <span className="font-semibold text-indigo-600">{mission?.title ?? `Mission #${t.mission_id}`}</span></>, time: new Date(t.created_at).getTime(), path: `/matching/${t.id}` });
    }
    for (const c of recentCandidates) {
      items.push({ id: `cand-${c.id}`, iconEl: <UserPlus size={15} className="text-indigo-500" />, bgClass: 'bg-indigo-100', text: <><span className="font-bold text-slate-900">{c.full_name}</span> ajouté à la base</>, time: new Date(c.created_at).getTime(), path: `/talents/${c.id}` });
    }
    for (const m of missions.slice(0, 5)) {
      items.push({ id: `mission-${m.id}`, iconEl: <Briefcase size={15} className="text-blue-500" />, bgClass: 'bg-blue-100', text: <>Mission <span className="font-semibold text-slate-900">{m.title}</span> — <span className="text-slate-500">{m.client}</span></>, time: new Date(m.datePosted).getTime(), path: `/missions/${m.id}`, badge: m.status || 'Nouveau', badgeClass: getStatusBadge(m.status) });
    }
    return items.sort((a, b) => b.time - a.time).slice(0, 8);
  }, [recentTasks, recentCandidates, missions]);

  const displayFeed = feedTab === 'portfolio' ? portfolioActivityFeed : orgActivityFeed;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="animate-in fade-in duration-500 pb-12">

      {/* HEADER */}
      <div className="mb-8">
        <div className="flex items-center gap-2 text-sm text-slate-500 font-medium mb-1 uppercase tracking-wide">
          <Calendar size={14} />{capitalizedDate}
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Bonjour, {firstName} 👋</h1>
        <p className="text-slate-500 mt-1">Pilotez vos recrutements depuis votre tableau de bord.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

        {/* ── LEFT COLUMN ──────────────────────────────────────────────────── */}
        <div className="lg:col-span-8 flex flex-col gap-8">

          {/* 4 KPI CARDS PORTEFEUILLE */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            {/* Mes Missions actives */}
            <div
              onClick={() => navigate('/missions')}
              className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all cursor-pointer group"
            >
              <div className="flex items-start justify-between mb-5">
                <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl group-hover:scale-110 transition-transform">
                  <Briefcase size={22} />
                </div>
                {newPortfolioMissions7d > 0 && (
                  <span className="text-[10px] font-bold px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">
                    +{newPortfolioMissions7d} (7j)
                  </span>
                )}
              </div>
              <div className="text-3xl font-bold text-slate-900 mb-1">
                {isPortfolioLoading ? <Loader2 size={22} className="animate-spin text-slate-300" /> : activePortfolioMissions.length}
              </div>
              <div className="text-sm text-slate-500 font-medium">Missions actives</div>
              <div className="text-xs text-slate-400 mt-1">dans mon portefeuille</div>
              <div className="mt-2 flex items-center gap-1 text-[11px] text-slate-400">
                <Building2 size={10} className="shrink-0" />
                <span>{orgName} : <span className="font-semibold text-slate-500">{activeMissions.length}</span></span>
              </div>
            </div>

            {/* Missions sans matching */}
            <div
              onClick={() => {
                const firstUnanalyzed = activePortfolioMissions.find(m => !analyzedMissionIds.has(String(m.id)));
                navigate('/matching', firstUnanalyzed ? { state: { preSelectedMissionId: Number(firstUnanalyzed.id) } } : {});
              }}
              className={`bg-white p-6 rounded-xl border shadow-sm hover:shadow-md transition-all cursor-pointer group ${portfolioMissionsWithoutAnalysis > 0 ? 'border-amber-200' : 'border-slate-200'}`}
            >
              <div className="flex items-start justify-between mb-5">
                <div className={`p-2.5 rounded-xl group-hover:scale-110 transition-transform ${portfolioMissionsWithoutAnalysis > 0 ? 'bg-amber-50 text-amber-600' : 'bg-slate-50 text-slate-400'}`}>
                  <TriangleAlert size={22} />
                </div>
                <Tooltip text="Missions actives du portefeuille sans aucune analyse IA lancée">
                  <Info size={13} className="text-slate-300 hover:text-slate-500 cursor-help mt-1" />
                </Tooltip>
              </div>
              <div className={`text-3xl font-bold mb-1 ${portfolioMissionsWithoutAnalysis > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                {isPortfolioLoading || matchingTasksCount === null
                  ? <Loader2 size={22} className="animate-spin text-slate-300" />
                  : portfolioMissionsWithoutAnalysis}
              </div>
              <div className="text-sm text-slate-500 font-medium">Missions sans matching</div>
              <div className="text-xs text-slate-400 mt-1">
                {portfolioMissionsWithoutAnalysis === 0 && 'Tout est analysé ✓'}
              </div>
              <div className="mt-2 flex items-center gap-1 text-[11px] text-slate-400">
                <Building2 size={10} className="shrink-0" />
                <span>{orgName} : <span className="font-semibold text-slate-500">{activeMissions.filter(m => !analyzedMissionIds.has(String(m.id))).length}</span></span>
              </div>
            </div>

            {/* Mes Talents */}
            <div
              onClick={() => navigate('/talents')}
              className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all cursor-pointer group"
            >
              <div className="flex items-start justify-between mb-5">
                <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl group-hover:scale-110 transition-transform">
                  <Users size={22} />
                </div>
                {(newPortfolioCandidates7d ?? 0) > 0 && (
                  <span className="text-[10px] font-bold px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">
                    +{newPortfolioCandidates7d} (7j)
                  </span>
                )}
              </div>
              <div className="text-3xl font-bold text-slate-900 mb-1">
                {portfolioCandidateCount === null ? <Loader2 size={22} className="animate-spin text-slate-300" /> : portfolioCandidateCount}
              </div>
              <div className="text-sm text-slate-500 font-medium">Talents</div>
              <div className="text-xs text-slate-400 mt-1">dans mon portefeuille</div>
              <div className="mt-2 flex items-center gap-1 text-[11px] text-slate-400">
                <Building2 size={10} className="shrink-0" />
                <span>{orgName} : <span className="font-semibold text-slate-500">{orgCandidateCount ?? '—'}</span></span>
              </div>
            </div>

            {/* Talents sans match > 80% */}
            <div
              onClick={() => navigate('/talents')}
              className={`bg-white p-6 rounded-xl border shadow-sm hover:shadow-md transition-all cursor-pointer group ${(portfolioCandidatesWithoutHighMatch ?? 0) > 0 ? 'border-rose-200' : 'border-slate-200'}`}
            >
              <div className="flex items-start justify-between mb-5">
                <div className={`p-2.5 rounded-xl group-hover:scale-110 transition-transform ${(portfolioCandidatesWithoutHighMatch ?? 0) > 0 ? 'bg-rose-50 text-rose-500' : 'bg-slate-50 text-slate-400'}`}>
                  <Target size={22} />
                </div>
                <Tooltip text="Talents du portefeuille sans aucun matching à plus de 80% — ils méritent peut-être de nouvelles missions">
                  <Info size={13} className="text-slate-300 hover:text-slate-500 cursor-help mt-1" />
                </Tooltip>
              </div>
              <div className={`text-3xl font-bold mb-1 ${(portfolioCandidatesWithoutHighMatch ?? 0) > 0 ? 'text-rose-500' : 'text-slate-400'}`}>
                {portfolioCandidatesWithoutHighMatch === null ? <Loader2 size={22} className="animate-spin text-slate-300" /> : portfolioCandidatesWithoutHighMatch}
              </div>
              <div className="text-sm text-slate-500 font-medium">Talents sans match &gt; 80%</div>
              <div className="text-xs text-slate-400 mt-1">
                {(portfolioCandidatesWithoutHighMatch ?? 0) === 0 && 'Tous bien matchés ✓'}
              </div>
              <div className="mt-2 flex items-center gap-1 text-[11px] text-slate-400">
                <Building2 size={10} className="shrink-0" />
                <span>{orgName} : <span className="font-semibold text-slate-500">{orgCandidatesWithoutHighMatch ?? '—'}</span></span>
              </div>
            </div>

          </div>

          {/* ACTIVITY FEED */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex flex-wrap gap-2 items-center justify-between">
              <h2 className="font-bold text-slate-900">Activité Récente</h2>
              <div className="flex items-center bg-slate-100 rounded-lg p-1 gap-1">
                <button
                  onClick={() => setFeedTab('portfolio')}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${feedTab === 'portfolio' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Mon Portefeuille
                </button>
                <button
                  onClick={() => setFeedTab('org')}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all flex items-center gap-1.5 ${feedTab === 'org' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  <Building2 size={11} />{orgName}
                </button>
              </div>
            </div>
            <div className="divide-y divide-slate-50">
              {displayFeed.length === 0 ? (
                <div className="p-8 text-center text-slate-400 text-sm">
                  <TrendingUp size={24} className="mx-auto mb-2 opacity-30" />
                  {feedTab === 'portfolio' ? 'Aucune activité dans votre portefeuille.' : 'Aucune activité récente.'}
                </div>
              ) : displayFeed.map(item => (
                <div
                  key={item.id}
                  onClick={() => item.path && navigate(item.path)}
                  className={`p-4 flex gap-4 hover:bg-slate-50 transition-colors group ${item.path ? 'cursor-pointer' : ''}`}
                >
                  <div className={`mt-1 w-8 h-8 rounded-full ${item.bgClass} flex items-center justify-center shrink-0 shadow-sm group-hover:scale-110 transition-transform`}>
                    {item.iconEl}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-slate-800">{item.text}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-slate-400 font-medium">{timeAgo(item.time)}</span>
                      {item.badge && (
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide ${item.badgeClass}`}>
                          {item.badge}
                        </span>
                      )}
                    </div>
                  </div>
                  {item.path && <div className="self-center"><ChevronRight size={16} className="text-slate-300 group-hover:text-slate-500 transition-colors" /></div>}
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* ── RIGHT COLUMN ─────────────────────────────────────────────────── */}
        <div className="lg:col-span-4 space-y-6">

          {/* ONBOARDING */}
          {showOnboarding && (
            <div className="bg-slate-900 rounded-xl p-6 shadow-lg text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500 rounded-full blur-3xl -mr-10 -mt-10 opacity-20" />
              <div className="flex justify-between items-center mb-4 relative z-10">
                <h3 className="font-bold text-lg">Premiers pas de l'organisation 🚀</h3>
                <span className="bg-slate-800 text-slate-300 text-xs font-bold px-2 py-1 rounded-md border border-slate-700">{completedCount}/{onboardingSteps.length}</span>
              </div>
              <div className="w-full bg-slate-800 rounded-full h-1.5 mb-6">
                <div className="bg-amber-500 h-1.5 rounded-full transition-all duration-500" style={{ width: `${(completedCount / onboardingSteps.length) * 100}%` }} />
              </div>
              <div className="space-y-2.5 relative z-10">
                {onboardingSteps.map((step, idx) => {
                  const isNext = !step.done && nextStep === step;
                  return (
                    <div key={idx} onClick={() => step.path && !step.done && navigate(step.path)}
                      className={`flex items-center justify-between gap-3 p-2.5 rounded-lg transition-all ${step.done ? 'bg-slate-800/50 border border-slate-700/50 opacity-60' : isNext ? 'bg-gradient-to-r from-amber-600/20 to-transparent border border-amber-600/50 cursor-pointer hover:bg-amber-600/30 group' : 'opacity-30'}`}
                    >
                      <div className="flex items-center gap-3">
                        {step.done ? <CheckCircle2 size={18} className="text-emerald-400 shrink-0" fill="currentColor" />
                          : isNext ? <div className="w-4 h-4 rounded-full border-2 border-amber-500 flex items-center justify-center shrink-0"><div className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" /></div>
                          : <div className="w-4 h-4 rounded-full border-2 border-slate-600 shrink-0" />}
                        <span className={`text-sm font-medium ${step.done ? 'line-through text-slate-400' : isNext ? 'text-white font-bold' : 'text-slate-400'}`}>{step.label}</span>
                      </div>
                      {isNext && <ChevronRight size={16} className="text-amber-500 group-hover:translate-x-1 transition-transform shrink-0" />}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* INDICATEURS CLÉS */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                <BarChart3 size={14} className="text-slate-400" /> Indicateurs clés
              </h3>
              <div className="flex items-center bg-slate-100 rounded-lg p-0.5 gap-0.5">
                <button
                  onClick={() => setIndicateursTab('portfolio')}
                  className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-all ${indicateursTab === 'portfolio' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Portefeuille
                </button>
                <button
                  onClick={() => setIndicateursTab('org')}
                  className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-all flex items-center gap-1 ${indicateursTab === 'org' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  <Building2 size={9} />{orgName}
                </button>
              </div>
            </div>
            <div className="divide-y divide-slate-50">
              {indicateursTab === 'portfolio' ? (<>
                <div className="px-4 py-3 flex items-center justify-between">
                  <span className="text-xs text-slate-500 flex items-center gap-1.5">
                    <Star size={11} className="text-amber-400" /> Meilleur score
                    <Tooltip text="Score de matching le plus élevé sur vos missions">
                      <Info size={11} className="text-slate-300 hover:text-slate-500 cursor-help" />
                    </Tooltip>
                  </span>
                  <span className="text-sm font-bold text-slate-900">{portfolioBestScore === null ? '—' : `${portfolioBestScore}%`}</span>
                </div>
                <div className="px-4 py-3 flex items-center justify-between">
                  <span className="text-xs text-slate-500 flex items-center gap-1.5">
                    <Target size={11} className="text-emerald-500" /> Pertinence moyenne
                    <Tooltip text="Score moyen des 100 derniers matchings sur vos missions">
                      <Info size={11} className="text-slate-300 hover:text-slate-500 cursor-help" />
                    </Tooltip>
                  </span>
                  <span className="text-sm font-bold text-slate-900">{portfolioAvgScore === null ? '—' : `${portfolioAvgScore}%`}</span>
                </div>
                <div className="px-4 py-3 flex items-center justify-between">
                  <span className="text-xs text-slate-500 flex items-center gap-1.5">
                    <BarChart3 size={11} className="text-blue-400" /> Taux de couverture
                    <Tooltip text="% des missions actives du portefeuille ayant au moins une analyse IA">
                      <Info size={11} className="text-slate-300 hover:text-slate-500 cursor-help" />
                    </Tooltip>
                  </span>
                  <span className={`text-sm font-bold ${portfolioCoverageRate >= 80 ? 'text-emerald-600' : portfolioCoverageRate >= 50 ? 'text-amber-600' : 'text-red-500'}`}>
                    {portfolioMatchingTasksCount === null ? '—' : `${portfolioCoverageRate}%`}
                  </span>
                </div>
                <div className="px-4 py-3 flex items-center justify-between">
                  <span className="text-xs text-slate-500 flex items-center gap-1.5">
                    <Zap size={11} className="text-amber-400" /> Analyses lancées
                  </span>
                  <span className="text-sm font-bold text-slate-900">{portfolioMatchingTasksCount === null ? '—' : portfolioMatchingTasksCount.toLocaleString('fr-FR')}</span>
                </div>
                <div className="px-4 py-3 flex items-center justify-between">
                  <span className="text-xs text-slate-500 flex items-center gap-1.5">
                    <Clock size={11} className="text-orange-400" /> Temps économisé
                    <Tooltip text="Estimation basée sur 20 min économisées par analyse IA lancée">
                      <Info size={11} className="text-slate-300 hover:text-slate-500 cursor-help" />
                    </Tooltip>
                  </span>
                  <span className="text-sm font-bold text-orange-600">{portfolioMatchingTasksCount === null ? '—' : portfolioTimeSavedDisplay}</span>
                </div>
              </>) : (<>
                <div className="px-4 py-3 flex items-center justify-between">
                  <span className="text-xs text-slate-500 flex items-center gap-1.5">
                    <Star size={11} className="text-amber-400" /> Meilleur score
                    <Tooltip text="Score de matching le plus élevé obtenu toutes missions confondues">
                      <Info size={11} className="text-slate-300 hover:text-slate-500 cursor-help" />
                    </Tooltip>
                  </span>
                  <span className="text-sm font-bold text-slate-900">{bestScore === null ? '—' : `${bestScore}%`}</span>
                </div>
                <div className="px-4 py-3 flex items-center justify-between">
                  <span className="text-xs text-slate-500 flex items-center gap-1.5">
                    <Target size={11} className="text-emerald-500" /> Pertinence moyenne
                    <Tooltip text="Score moyen des 100 derniers matchings de l'organisation">
                      <Info size={11} className="text-slate-300 hover:text-slate-500 cursor-help" />
                    </Tooltip>
                  </span>
                  <span className="text-sm font-bold text-slate-900">{avgScore === null ? '—' : `${avgScore}%`}</span>
                </div>
                <div className="px-4 py-3 flex items-center justify-between">
                  <span className="text-xs text-slate-500 flex items-center gap-1.5">
                    <BarChart3 size={11} className="text-blue-400" /> Taux de couverture
                    <Tooltip text="% des missions actives de l'organisation ayant au moins une analyse IA">
                      <Info size={11} className="text-slate-300 hover:text-slate-500 cursor-help" />
                    </Tooltip>
                  </span>
                  <span className={`text-sm font-bold ${coverageRate >= 80 ? 'text-emerald-600' : coverageRate >= 50 ? 'text-amber-600' : 'text-red-500'}`}>
                    {matchingTasksCount === null ? '—' : `${coverageRate}%`}
                  </span>
                </div>
                <div className="px-4 py-3 flex items-center justify-between">
                  <span className="text-xs text-slate-500 flex items-center gap-1.5">
                    <Zap size={11} className="text-amber-400" /> Analyses lancées
                  </span>
                  <span className="text-sm font-bold text-slate-900">{matchingTasksCount === null ? '—' : matchingTasksCount.toLocaleString('fr-FR')}</span>
                </div>
                <div className="px-4 py-3 flex items-center justify-between">
                  <span className="text-xs text-slate-500 flex items-center gap-1.5">
                    <Clock size={11} className="text-orange-400" /> Temps économisé
                    <Tooltip text="Estimation basée sur 20 min économisées par analyse IA lancée">
                      <Info size={11} className="text-slate-300 hover:text-slate-500 cursor-help" />
                    </Tooltip>
                  </span>
                  <span className="text-sm font-bold text-orange-600">{matchingTasksCount === null ? '—' : timeSavedDisplay}</span>
                </div>
              </>)}
            </div>
          </div>

          {/* QUICK ACTIONS */}
          <div className="flex flex-col gap-3">
            <button onClick={() => navigate('/missions', { state: { openAdd: true } })}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white p-4 rounded-xl font-bold shadow-md shadow-amber-900/10 flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5">
              <Plus size={20} />Nouvelle Mission
            </button>
            <button onClick={() => navigate('/talents', { state: { openAdd: true } })}
              className="w-full bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 p-4 rounded-xl font-bold shadow-sm flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5">
              <UploadCloud size={20} className="text-indigo-600" />Importer des CVs
            </button>
            <button onClick={() => navigate('/matching')}
              className="w-full py-2 text-sm font-semibold text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors flex items-center justify-center gap-2">
              <Zap size={16} />Accès Matching Rapide
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default HomeDashboardView;
